import type { SetGroup } from "./setGroups";
import type { Lap } from "./laps";

export interface PeakHrContext {
  setLabel: string | null;
  stroke: string | null;
}

/**
 * Determines which set and stroke the peak HR timestamp fell in.
 *
 * Window convention: half-open [startMs, endMs) — exactly like setHrWindow.ts.
 *
 * setLabel logic:
 *   - Find the SetGroup whose [startMs, endMs) contains peakHrTs.
 *   - If none (peak fell in a rest gap between sets), find the NEAREST set
 *     by smallest time distance to ANY point in the set window:
 *       dist = max(0, max(startMs - peakHrTs, peakHrTs - endMs))
 *     i.e. if peakHrTs is left of the window, distance = startMs - peakHrTs;
 *          if right of the window, distance = peakHrTs - endMs;
 *          if inside, distance = 0.
 *   - Use set.label as the display string.
 *   - If setGroups is empty, setLabel = null.
 *
 * stroke logic:
 *   - Find the Lap (from the flat laps array) whose [startMs, endMs) contains peakHrTs.
 *   - Use lap.strokeStyle.
 *   - If strokeStyle is "Unknown" OR no lap contains the timestamp, stroke = null.
 *
 * If peakHrTs is null → { setLabel: null, stroke: null }.
 */
export function peakHrContext(
  peakHrTs: number | null,
  setGroups: SetGroup[],
  laps: Lap[],
): PeakHrContext {
  if (peakHrTs === null) return { setLabel: null, stroke: null };

  // setLabel: find containing window, fall back to nearest
  let setLabel: string | null = null;
  if (setGroups.length > 0) {
    const containing = setGroups.find(
      (sg) => peakHrTs >= sg.startMs && peakHrTs < sg.endMs,
    );
    if (containing) {
      setLabel = containing.label;
    } else {
      // nearest by distance to window
      let bestDist = Infinity;
      let bestLabel: string | null = null;
      for (const sg of setGroups) {
        const dist =
          peakHrTs < sg.startMs
            ? sg.startMs - peakHrTs
            : peakHrTs >= sg.endMs
              ? peakHrTs - sg.endMs
              : 0;
        if (dist < bestDist) {
          bestDist = dist;
          bestLabel = sg.label;
        }
      }
      setLabel = bestLabel;
    }
  }

  // stroke: find the containing lap
  const containingLap = laps.find(
    (l) => peakHrTs >= l.startMs && peakHrTs < l.endMs,
  );
  const stroke =
    containingLap && containingLap.strokeStyle !== "Unknown"
      ? containingLap.strokeStyle
      : null;

  return { setLabel, stroke };
}
